import { AgentStatus } from "@superblocksteam/shared";
import { orgIsCloud } from "store/slices/organizations/utils";
import { signResource, verifyResources } from "utils/resource-signing";
import type {
  Agent,
  ApiToSign,
  ApiToVerify,
  AppToSign,
  AppToVerify,
  ClientMethods,
  MethodHandlers,
  Organization,
  ServerMethods,
  Signature,
} from "@superblocksteam/shared";
import type { Api } from "store/slices/apisV2/backend-types";

export function createRequestHandlers(
  agents: Agent[],
  organization: Organization,
): MethodHandlers<ClientMethods, ServerMethods, unknown> {
  if (orgIsCloud(organization)) {
    return {
      v1: {
        signing: {
          signApplication: [
            () => {
              throw new Error("Signing not implemented");
            },
          ],
          signApis: [
            () => {
              throw new Error("Signing not implemented");
            },
          ],
          verifyApplication: [
            () => {
              throw new Error("Signing not implemented");
            },
          ],
          verifyApi: [
            () => {
              throw new Error("Signing not implemented");
            },
          ],
        },
      },
    };
  }
  const requestHandlers: MethodHandlers<ClientMethods, ServerMethods, unknown> =
    {
      v1: {
        signing: {
          signApplication: [
            async ({
              branchName,
              toSign,
            }: {
              branchName: string;
              toSign: AppToSign;
            }) => {
              return await signResource({
                agents,
                organization,
                branchName,
                resource: {
                  literal: {
                    data: toSign.rootHash,
                  },
                },
              });
            },
          ],

          signApis: [
            async ({
              branchName,
              toSign,
            }: {
              branchName: string;
              toSign: ApiToSign[];
            }) => {
              // max number of requests at a time
              const batchSize = 6;
              const activeAgents = agents.filter(
                (a) => a.status === AgentStatus.ACTIVE,
              );
              const signatures: Signature[] = [];

              for (let i = 0; i < toSign.length; i += batchSize) {
                // Take the next batch
                const batch = toSign.slice(i, i + batchSize);

                // Fire off this batch in parallel
                const batchResults = await Promise.all(
                  batch.map(async (item, idx) => {
                    // pick the agent, round-robin style
                    const agent = activeAgents[(i + idx) % activeAgents.length];
                    const r = await signResource({
                      agents: [agent],
                      organization,
                      branchName,
                      resource: { api: item.apiPb as Api },
                    });
                    return r.signature;
                  }),
                );

                // Collect them before moving to the next batch
                signatures.push(...batchResults);
              }
              return { signatures };
            },
          ],
          verifyApplication: [
            async ({
              branchName,
              toVerify,
            }: {
              branchName: string;
              toVerify: AppToVerify;
            }) => {
              try {
                await verifyResources({
                  agents,
                  organization,
                  branchName,
                  resources: [
                    {
                      literal: {
                        data: toVerify.rootHash,
                        signature: toVerify.signature,
                      },
                    },
                  ],
                });
                return { ok: true };
              } catch {
                return { ok: false };
              }
            },
          ],

          verifyApi: [
            async ({
              branchName,
              toVerify,
            }: {
              branchName: string;
              toVerify: ApiToVerify[];
            }) => {
              try {
                await verifyResources({
                  agents,
                  organization,
                  branchName,
                  resources: toVerify.map(({ apiPb }) => ({
                    api: apiPb as Api,
                  })),
                });
                return { ok: true };
              } catch {
                return { ok: false };
              }
            },
          ],
        },
      },
    };
  return requestHandlers;
}
